'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import Button from 'src/components/primitives/Button/Button';
import Divider from 'src/components/primitives/Divider/Divider';
import Select from 'src/components/primitives/Select/Select';
import { ComboBox } from 'src/components/primitives/ComboBox/ComboBox';
import MarketIcon from 'src/components/Utils/MarketIcon';
import FunXyzAssetIcon from 'src/components/Utils/FunXyzAssetIcon';
import Input from 'src/components/primitives/Input/Input';
import { CollateralInputV2 } from 'src/components/Inputs/CollateralInput';
import { InputQuickActions } from 'src/components/Inputs/InputQuickActions';
import SubAccountSelector from 'src/components/Modals/CollateralModal/components/SubAccountSelector';
import useModal from './hooks/useModal';
import useCollateralModalState from 'src/components/Modals/CollateralModal/hooks/useCollateralModalState';
import useAccountTargetSpotBalance from 'src/hooks/useAccountTargetSpotBalance';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import useWithdrawToOtherDestinations from 'src/hooks/useWithdrawToOtherDestinations';
import { getChainIconSymbol } from 'src/constants/funXyzChains';
import { BigNum } from '@drift-labs/sdk';
import { CollateralModalProps } from 'src/components/Modals/CollateralModal/types';
import { cj } from 'src/utils/classNames';
import AddressBookSearchDropdown, {
	AddressBookTrigger,
} from 'src/components/AddressBook/AddressBookSearchDropdown';
import type { WhitelistRecord } from 'src/hooks/query/useWhitelistAddresses';

export default function WithdrawToOtherDestinationsModal(
	props: CollateralModalProps
) {
	const { hide } = useModal();

	const subaccounts = useLazySubAccounts();
	const hasMultipleSubaccounts = subaccounts.length >= 2;

	const {
		selectedCollateralMarket,
		selectedTargetAccountKey,
		selectedPoolId,
		handleTargetAccountChange,
		updateCollateralIndex,
	} = useCollateralModalState(props);

	const targetCollatBalances = useAccountTargetSpotBalance(
		selectedCollateralMarket,
		selectedTargetAccountKey,
		true
	);

	const withdrawalLimit = targetCollatBalances.withdrawLimitBase.shiftTo(
		selectedCollateralMarket.precisionExp
	);

	// Amount
	const [amount, setAmount] = useState('');
	const [isMax, setIsMax] = useState(false);

	const amountBigNum = isMax
		? targetCollatBalances.withdrawLimitBase
		: BigNum.fromPrint(amount || '0', selectedCollateralMarket.precisionExp);

	const handleAmountChange = useCallback(
		(newValue?: string) => {
			if (newValue === undefined) return;
			setIsMax(false);

			if (newValue === '') {
				setAmount('');
				return;
			}

			const inputBigNum = BigNum.fromPrint(
				newValue,
				selectedCollateralMarket.precisionExp
			);
			if (inputBigNum.lte(targetCollatBalances.withdrawLimitBase)) {
				setAmount(newValue);
			} else {
				setAmount(targetCollatBalances.withdrawLimitBase.print());
				setIsMax(true);
			}
		},
		[
			selectedCollateralMarket.precisionExp,
			targetCollatBalances.withdrawLimitBase,
		]
	);

	const handleMax = useCallback(() => {
		setAmount(withdrawalLimit.toTradePrecision());
		setIsMax(true);
	}, [withdrawalLimit]);

	// Keep max in sync
	const withdrawalLimitStr = withdrawalLimit.toTradePrecision();
	useEffect(() => {
		if (isMax) {
			setAmount(withdrawalLimitStr);
		}
	}, [isMax, withdrawalLimitStr]);

	// Reset on market/account change
	useEffect(() => {
		setAmount('');
		setIsMax(false);
	}, [selectedCollateralMarket?.symbol, selectedTargetAccountKey]);

	const {
		supportedSourceBanks,
		uniqueAssets,
		selectedReceivingAssetSymbol,
		setSelectedReceivingAssetSymbol,
		chainOptions,
		selectedChainId,
		setSelectedChainId,
		handleChainSelect,
		selectedChain,
		quote,
		isGettingQuote,
		recipientAddress,
		setRecipientAddress,
		isValidRecipientAddress,
		isSubmitting,
		canSubmit,
		isBelowMinimumWithdrawal,
		handleSubmit,
		estimatedReceiveAmount,
	} = useWithdrawToOtherDestinations({
		selectedCollateralMarket,
		amountBigNum,
	});

	const [assetSearchQuery, setAssetSearchQuery] = useState('');
	const [assetComboOpen, setAssetComboOpen] = useState(false);

	const filteredAssets = useMemo(() => {
		if (!assetSearchQuery) return uniqueAssets;
		const query = assetSearchQuery.toLowerCase().trim();
		return uniqueAssets.filter((asset) =>
			asset.symbol.toLowerCase().includes(query)
		);
	}, [uniqueAssets, assetSearchQuery]);

	const selectedReceivingAsset = useMemo(
		() =>
			uniqueAssets.find(
				(a) =>
					a.symbol.toUpperCase() === selectedReceivingAssetSymbol.toUpperCase()
			),
		[uniqueAssets, selectedReceivingAssetSymbol]
	);

	const stepOffset = hasMultipleSubaccounts ? 1 : 0;

	const sizePresets = (
		<InputQuickActions.SizePresets
			onClick={(val) => {
				if (val.eq(withdrawalLimit)) {
					handleMax();
				} else {
					handleAmountChange(val.print());
				}
			}}
			maxAmount={withdrawalLimit}
		/>
	);

	const onSubmit = async () => {
		const result = await handleSubmit();
		if (result) {
			hide();
		}
	};

	const handleAddressBookSelect = useCallback(
		(record: WhitelistRecord) => {
			setRecipientAddress(record.address);
			if (record.chainId) {
				setSelectedChainId(record.chainId);

				if (record.token) {
					setSelectedReceivingAssetSymbol(record.token);
				}
			}
		},
		[setRecipientAddress, setSelectedChainId, setSelectedReceivingAssetSymbol]
	);

	const handleAssetSelect = useCallback(
		(symbol: string) => {
			setSelectedReceivingAssetSymbol(symbol);
			setAssetComboOpen(false);
			setAssetSearchQuery('');
		},
		[setSelectedReceivingAssetSymbol]
	);

	const buttonText = isBelowMinimumWithdrawal
		? 'Minimum withdrawal: $30'
		: !recipientAddress || !amount
			? 'Enter Recipient Address & Amount'
			: 'Confirm Withdraw';

	return (
		<BaseModal.Container
			onClose={hide}
			// 520px instead of 480px so long addresses (EVM 42-char, Bitcoin segwit) fit on one line
			className="sm:min-w-[520px]"
		>
			<BaseModal.Title onClose={hide}>Withdraw</BaseModal.Title>
			<BaseModal.Body>
				{/* 1. Withdraw From (conditional) */}
				{hasMultipleSubaccounts && (
					<div className="flex flex-col gap-2">
						<span className="typo-t4 text-text-default">1. Withdraw From</span>
						<SubAccountSelector
							mode="withdraw"
							selectedTargetAccountKey={selectedTargetAccountKey}
							selectedPoolId={selectedPoolId}
							handleTargetAccountChange={handleTargetAccountChange}
							accountLabel="Account"
						/>
					</div>
				)}

				{/* Withdraw Asset & Amount */}
				<div className="flex flex-col gap-2">
					<span className="typo-t4 text-text-default">
						{1 + stepOffset}. Withdraw Asset & Amount
					</span>

					<CollateralInputV2
						source={{
							type: 'userAccount',
							userKey: selectedTargetAccountKey,
						}}
						selectedMarket={selectedCollateralMarket}
						maxAmount={targetCollatBalances.withdrawLimitBase}
						onChangeValue={handleAmountChange}
						onChangeMarket={(market) =>
							updateCollateralIndex(market.marketIndex)
						}
						value={
							isMax
								? targetCollatBalances.withdrawLimitBase.toTradePrecision()
								: amount
						}
						label="Amount"
						customElementRight={sizePresets}
						defaultToHighestBalance
						poolId={selectedPoolId}
						banks={supportedSourceBanks}
						error={
							isBelowMinimumWithdrawal ? 'Minimum withdrawal: $30' : undefined
						}
						// We currently only support 3 assets so hiding the search bar for now.
						noSearch
					/>
				</div>

				{/* Receiving Asset & Chain */}
				<div className="flex flex-col gap-2">
					<span className="typo-t4 text-text-default">
						{2 + stepOffset}. Receiving Asset & Chain
					</span>

					<div className="grid grid-cols-2 gap-x-sm">
						{/* Asset ComboBox */}
						<ComboBox.Root
							open={assetComboOpen}
							onOpenChange={setAssetComboOpen}
						>
							<ComboBox.Trigger label="Asset">
								<div className="flex items-center gap-x-xs overflow-hidden">
									<FunXyzAssetIcon
										logo={selectedReceivingAsset?.logo}
										symbol={selectedReceivingAssetSymbol}
										className="size-lg shrink-0"
									/>
									<span className="truncate">
										{selectedReceivingAssetSymbol}
									</span>
								</div>
							</ComboBox.Trigger>
							<ComboBox.Options
								id="withdraw-asset-select"
								query={assetSearchQuery}
								setQuery={setAssetSearchQuery}
								value={selectedReceivingAssetSymbol}
								onValueChange={handleAssetSelect}
								searchBarPlaceholder="Search assets..."
								className="w-[240px]"
								scrollContainerClassName="max-h-[280px]"
								align="start"
							>
								{filteredAssets.map((asset) => (
									<ComboBox.Option
										key={asset.symbol}
										value={asset.symbol}
										isSelected={asset.symbol === selectedReceivingAssetSymbol}
										onValueChange={handleAssetSelect}
									>
										<div className="flex items-center gap-x-xs overflow-hidden">
											<FunXyzAssetIcon
												logo={asset.logo}
												symbol={asset.symbol}
												className="size-lg shrink-0"
											/>
											<span className="truncate">{asset.symbol}</span>
										</div>
									</ComboBox.Option>
								))}
							</ComboBox.Options>
						</ComboBox.Root>

						{/* Chain Select */}
						<Select.Root
							value={selectedChainId ?? ''}
							onValueChange={handleChainSelect}
						>
							<Select.Trigger label="Chain">
								{selectedChain && (
									<div className="flex items-center gap-x-xs overflow-hidden">
										<MarketIcon
											marketSymbol={getChainIconSymbol(selectedChain.id) ?? ''}
											className="size-lg shrink-0"
										/>
										<span className="truncate">{selectedChain.name}</span>
									</div>
								)}
							</Select.Trigger>
							<Select.Options className="max-h-[280px] w-[240px]" align="start">
								{chainOptions.map((chain) => (
									<Select.Option
										key={chain.id}
										value={chain.id}
										disabled={!chain.isAvailable}
									>
										<div className="flex items-center gap-x-xs overflow-hidden">
											<MarketIcon
												marketSymbol={getChainIconSymbol(chain.id) ?? ''}
												className={cj(
													'size-lg shrink-0',
													!chain.isAvailable && 'opacity-50'
												)}
											/>
											<span
												className={cj(
													'truncate',
													!chain.isAvailable && 'text-text-disabled'
												)}
											>
												{chain.name}
											</span>
										</div>
									</Select.Option>
								))}
							</Select.Options>
						</Select.Root>
					</div>
				</div>

				{/* Recipient Address */}
				<div className="flex flex-col gap-2">
					<span className="typo-t4 text-text-default">
						{3 + stepOffset}. Recipient Address
					</span>
					<AddressBookSearchDropdown onSelect={handleAddressBookSelect}>
						<Input
							type="text"
							labelVariant="none"
							value={recipientAddress}
							onChange={setRecipientAddress}
							placeholder="Paste the address here"
							suffix={<AddressBookTrigger />}
						/>
					</AddressBookSearchDropdown>
					{recipientAddress && !isValidRecipientAddress && (
						<span className="typo-b5 text-negative-red">
							{selectedChain
								? `Enter a valid ${selectedChain.name} address`
								: 'Invalid address format'}
						</span>
					)}
				</div>

				<Divider />

				{/* Summary */}
				<div className="flex flex-col gap-1">
					<div className="flex items-center justify-between">
						<span>You Will Receive</span>
						<span className="text-text-default">
							{isGettingQuote
								? '...'
								: estimatedReceiveAmount !== undefined
									? `${parseFloat(estimatedReceiveAmount.toFixed(6))} ${selectedReceivingAssetSymbol}`
									: '-'}
						</span>
					</div>
					<div className="flex items-center justify-between">
						<span>Price Impact</span>
						<span className="text-text-default">
							{isGettingQuote
								? '...'
								: quote?.estFeeBreakdown?.totalImpact !== undefined
									? `~${quote.estFeeBreakdown.totalImpact.toFixed(2)}%`
									: '-'}
						</span>
					</div>
				</div>
			</BaseModal.Body>
			<BaseModal.Footer>
				<Button
					className="w-full"
					size="lg"
					onClick={onSubmit}
					disabled={!canSubmit}
					isLoading={isSubmitting}
				>
					{buttonText}
				</Button>
			</BaseModal.Footer>
		</BaseModal.Container>
	);
}
